import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import SearchableDropdown from '../../../components/common/SearchableDropdown';

const WarehouseEntryForm = () => {
  const [formData, setFormData] = useState({
    order_id: '',
    cha_cma_id: '',
    warehouse_id: '',
    commodity_id: '',
    received_date: '',
    received_qty_mt: '',
    qc_status: '',
    grade: '',
    dockage_pct: '',
    psb_pct: '',
    fm_pct: '',
    unit_cost_inr: '',
    check_cma_cha: 'CMA' // Added this field to track CMA/CHA selection
  });
  const [warehouses, setWarehouses] = useState([]);
  const [cmaList, setCmaList] = useState([]);
  const [chaList, setChaList] = useState([]);
  const [chaWarehouses, setChaWarehouses] = useState([]);
  const [importOrders, setImportOrders] = useState([]);
  const [commodityOptions, setCommodityOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [selectedType, setSelectedType] = useState('CMA');
  const navigate = useNavigate();

  // Fetch CMA list, CHA list and import orders for dropdown
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch CMA list
        const cmaRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-cma-list`, {
          method: 'GET',
          credentials: 'include',
        });
        if (cmaRes.ok) {
          const cmaData = await cmaRes.json();
          setCmaList(cmaData.data || []);
        }

        // Fetch CHA list
        const chaRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-cha-list`, {
          method: 'GET',
          credentials: 'include',
        });
        if (chaRes.ok) {
          const chaData = await chaRes.json();
          setChaList(chaData.data || []);
        }

        // Fetch import orders
        const importRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-import-order`, {
          method: 'GET',
          credentials: 'include',
        });
        if (importRes.ok) {
          const importData = await importRes.json();
          setImportOrders(importData.data || []);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchCommodities = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-commodities`, {
          method: 'GET',
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setCommodityOptions(data.data || []);
        }
      } catch (error) {
        console.error('Failed to fetch commodities:', error);
        setCommodityOptions([]);
      }
    };

    fetchCommodities();
  }, []);

  // Fetch warehouses by CMA
  const fetchWarehousesByCma = async (cmaId) => {
    if (!cmaId) {
      setWarehouses([]);
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-warehouse-list-by-cma?cha_cma_id=${cmaId}`, {
        method: 'GET',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setWarehouses(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching CMA warehouses:', error);
      setWarehouses([]);
    }
  };

  // Fetch warehouses by CHA
  const fetchWarehousesByCha = async (chaId) => {
    if (!chaId) {
      setChaWarehouses([]);
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-warehouse-list-by-cha?cha_cma_id=${chaId}`, {
        method: 'GET',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setChaWarehouses(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching CHA warehouses:', error);
      setChaWarehouses([]);
    }
  };

  // Handle type change (CMA/CHA)
  const handleTypeChange = (type) => {
    setSelectedType(type);
    setFormData(prev => ({ 
      ...prev, 
      cha_cma_id: '', 
      check_cma_cha: type,
      warehouse_id: ''
    }));
    setWarehouses([]);
    setChaWarehouses([]);
  };

  const getMaxDate = () => new Date().toISOString().split('T')[0]; // YYYY-MM-DD

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'received_date' && value) {
      const maxDate = getMaxDate();
      if (value > maxDate) {
        setMessage({ type: 'error', text: 'Future dates are not allowed for Received Date.' });
        return;
      }
    }
    if (name === 'received_date') {
      setMessage(prev => (prev?.type === 'error' && prev?.text === 'Future dates are not allowed for Received Date.' ? null : prev));
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDropdownChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'cha_cma_id') {
      if (selectedType === 'CMA') {
        fetchWarehousesByCma(value);
      } else {
        fetchWarehousesByCha(value);
      }
      setFormData(prev => ({ ...prev, warehouse_id: '' })); // clear warehouse selection
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const maxDate = getMaxDate();
    if (formData.received_date && formData.received_date > maxDate) {
      setMessage({ type: 'error', text: 'Future dates are not allowed for Received Date.' });
      return;
    }

    setLoading(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/warehouse/warehouse-entry-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        setMessage({ type: 'success', text: 'Warehouse Entry Created Successfully!' });
        setFormData({
          order_id: '',
          cha_cma_id: '',
          warehouse_id: '',
          commodity_id: '',
          received_date: '',
          received_qty_mt: '',
          qc_status: '',
          grade: '',
          dockage_pct: '',
          psb_pct: '',
          fm_pct: '',
          unit_cost_inr: '',
          check_cma_cha: 'CMA'
        });
        setSelectedType('CMA');
      } else {
        setMessage({ type: 'error', text: data.message || 'Something went wrong' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Server Error' });
    }
    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-4 bg-white">
      <div className="flex justify-between items-center border-b pb-3 mb-5">
        <h2 className="text-lg font-medium text-gray-800">Vessel to Storage Movement</h2>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#10a5e9] border border-[#10a5e9] px-3 py-1 text-xs rounded font-normal hover:bg-[#10a5e9] hover:text-white hover:border-[#10a5e9] transition"
        >
          <FaArrowLeft />
          Back
        </button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-700 mb-1">Purchase Order</label>
            <SearchableDropdown
              options={importOrders.map(order => ({
                id: order.order_id,
                name: `${order.purchase_contract_no || 'N/A'} (${order.vessel_name || 'N/A'})`
              }))}
              value={formData.order_id}
              onChange={(value) => handleDropdownChange('order_id', value)}
              placeholder="Select Import Order"
              displayKey="name"
              valueKey="id"
              required
            />
          </div>

          {/* CMA/CHA Checkbox Section */}
          <div className="md:col-span-2 flex gap-6 items-center mb-2">
            <label className="flex items-center gap-1">
              <input 
                type="checkbox" 
                checked={selectedType === 'CMA'} 
                onChange={() => handleTypeChange('CMA')} 
              /> 
              CMA
            </label>
            <label className="flex items-center gap-1">
              <input 
                type="checkbox" 
                checked={selectedType === 'CHA'} 
                onChange={() => handleTypeChange('CHA')} 
              /> 
              CHA
            </label>
          </div>

          {/* Conditional CMA Dropdown */}
          {selectedType === 'CMA' && (
            <div>
              <label className="block text-sm text-gray-700 mb-1">CMA</label>
              <SearchableDropdown
                options={cmaList
                  .filter(cma => cma.type === 'CMA' || cma.type === 'BOTH')
                  .map(cma => ({
                    id: cma.cha_cma_id,
                    name: cma.name
                  }))}
                value={formData.cha_cma_id}
                onChange={(value) => handleDropdownChange('cha_cma_id', value)}
                placeholder="Select CMA"
                displayKey="name"
                valueKey="id"
                required
              />
            </div>
          )}

          {/* Conditional CHA Dropdown */}
          {selectedType === 'CHA' && (
            <div>
              <label className="block text-sm text-gray-700 mb-1">CHA</label>
              <SearchableDropdown
                options={chaList
                  .filter(cha => cha.type === 'CHA' || cha.type === 'BOTH')
                  .map(cha => ({
                    id: cha.cha_cma_id,
                    name: cha.name
                  }))}
                value={formData.cha_cma_id}
                onChange={(value) => handleDropdownChange('cha_cma_id', value)}
                placeholder="Select CHA"
                displayKey="name"
                valueKey="id"
                required
              />
            </div>
          )}

          {/* Conditional Warehouse Dropdown for CMA */}
          {selectedType === 'CMA' && (
            <div>
              <label className="block text-sm text-gray-700 mb-1">Warehouse</label>
              <SearchableDropdown
                options={warehouses.map(warehouse => ({
                  id: warehouse.warehouse_id,
                  name: `${warehouse.name} (${warehouse.warehouse_location || 'N/A'})`
                }))}
                value={formData.warehouse_id}
                onChange={(value) => handleDropdownChange('warehouse_id', value)}
                placeholder="Select Warehouse"
                displayKey="name"
                valueKey="id"
                required
              />
            </div>
          )}

          {/* Conditional Warehouse Dropdown for CHA */}
          {selectedType === 'CHA' && (
            <div>
              <label className="block text-sm text-gray-700 mb-1">Warehouse/Port</label>
              <SearchableDropdown
                options={chaWarehouses.map(warehouse => ({
                  id: warehouse.warehouse_id,
                  name: `${warehouse.name || warehouse.warehouse_name || warehouse.port_name} ${warehouse.location ? `(${warehouse.location})` : ''}`
                }))}
                value={formData.warehouse_id}
                onChange={(value) => handleDropdownChange('warehouse_id', value)}
                placeholder="Select Warehouse/Port"
                displayKey="name"
                valueKey="id"
                required
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-medium mb-1">Commodity</label>
            <SearchableDropdown
              options={commodityOptions}
              value={formData.commodity_id}
              onChange={value => handleDropdownChange('commodity_id', value)}
              placeholder="Select Commodity"
              displayKey="name"
              valueKey="commodity_id"
              required
              customDisplay={(item) => `${item.name} (${item.origin || 'N/A'})`}
            />
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">Received Date</label>
            <input 
              type="date" 
              name="received_date" 
              value={formData.received_date} 
              onChange={handleChange} 
              onKeyDown={(e) => { if (e.key !== 'Tab' && e.key !== 'Escape') e.preventDefault(); }}
              max={getMaxDate()}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none cursor-pointer" 
              title="Select date from calendar only (future dates not allowed)"
              required 
              autoComplete="off" 
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Received Quantity (MT)</label>
            <input 
              type="number" 
              step="0.001"
              name="received_qty_mt" 
              value={formData.received_qty_mt} 
              onChange={handleChange} 
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" 
              required 
              autoComplete="off" 
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">QC Status</label>
            <select 
              name="qc_status" 
              value={formData.qc_status} 
              onChange={handleChange} 
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" 
              required
            >
              <option value="">Select QC Status</option>
              <option value="PASSED">PASSED</option>
              <option value="HOLD">HOLD</option>
              <option value="REJECTED">REJECTED</option>
            </select>
          </div>
          {/* Grade field - Made optional by removing required attribute */}
          <div>
            <label className="block text-sm text-gray-700 mb-1">Grade</label>
            <input 
              type="text" 
              name="grade" 
              value={formData.grade} 
              onChange={handleChange} 
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" 
              autoComplete="off" 
            />
          </div>
          {/* Dockage % field - Made optional by removing required attribute */}
          <div>
            <label className="block text-sm text-gray-700 mb-1">Dockage %</label>
            <input 
              type="number" 
              step="0.01"
              name="dockage_pct" 
              value={formData.dockage_pct} 
              onChange={handleChange} 
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" 
              autoComplete="off" 
            />
          </div>
          {/* PSB % field - Made optional by removing required attribute */}
          <div>
            <label className="block text-sm text-gray-700 mb-1">PSB %</label>
            <input 
              type="number" 
              step="0.01"
              name="psb_pct" 
              value={formData.psb_pct} 
              onChange={handleChange} 
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" 
              autoComplete="off" 
            />
          </div>
          {/* FM % field - Made optional by removing required attribute */}
          <div>
            <label className="block text-sm text-gray-700 mb-1">FM %</label>
            <input 
              type="number" 
              step="0.01"
              name="fm_pct" 
              value={formData.fm_pct} 
              onChange={handleChange} 
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" 
              autoComplete="off" 
            />
          </div>
          {/* Unit Cost (INR) field - Made optional by removing required attribute */}
          <div>
            <label className="block text-sm text-gray-700 mb-1">Unit Cost (INR)</label>
            <input 
              type="number" 
              step="0.01"
              name="unit_cost_inr" 
              value={formData.unit_cost_inr} 
              onChange={handleChange} 
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none" 
              autoComplete="off" 
            />
          </div>
        </div>
        <div className="pt-2 flex items-center gap-3">
          <button 
            type="submit" 
            className="px-4 py-2 text-xs bg-[#10a5e9] text-white font-normal rounded border border-[#10a5e9] hover:bg-[#0e8ac2] transition disabled:opacity-60" 
            style={{ minWidth: '110px' }} 
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Entry'}
          </button>
          {message && (
            <div className={`text-sm ${message.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
              {message.text}
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default WarehouseEntryForm;
